import { formatPrice } from '@ynap/intl-formatters'
import { priceFromPriceObj, subtractPriceObjs, addPriceObjs } from '@ynap/price-utils'
import { getShippingPrice, getShippingOptions } from '@ynap/cart-state'
import getStoreCredit from './getStoreCredit'
import getPromotionsAndRewards from './getPromotionsAndRewards'
import type { Locale, StoreState } from 'app/state/types'
import type { Amount, Messages, ShoppingCart, TaxByTaxCategory } from 'app/types/common-types'

interface State extends StoreState {
    cart: ShoppingCart
}

type Props = {
    messages: Messages
}

const getTotals = (state: State, props: Props) => {
    const { messages } = props
    const { locale = {} as Locale, cart = {} as ShoppingCart, config } = state

    const {
        head: {
            flags: { lineLevelTaxesEnabled, showUsTaxUi },
        },
    } = config

    const {
        totalShippingCharge,
        grandTotal,
        grandTotalCurrency,
        promotionCode = [],
        adjustment = [],
        x_totalSavings,
        totalSalesTax,
        totalShippingTax,
        x_sumLineTotal,
        x_totalDDP,
        x_taxType,
        x_deliveryDuty,
        x_totalProductPriceWithPromotion,
        totalTaxByTaxCategory,
        x_totalShippingChargeWithAdjustment,
        totalShippingChargeCurrency,
        x_adjustedTotalShippingChargeIncTax,
        totalProductPrice,
        totalAdjustment,
    } = cart

    const taxDutyInfo = () => {
        if (locale.country === 'US') {
            return messages?.totals.amTaxInfo()
        } else if (x_taxType === 'DDP' || x_taxType === 'DDP_INCLUDED') {
            return messages?.totals.shoppingBagDDPInfo({ country: locale.country })
        } else if (x_deliveryDuty === 'DDU') {
            return messages?.totals.dduInfo()
        }
    }

    const format = (price: Amount | undefined): string | undefined => {
        const rawPrice = priceFromPriceObj(price)
        return rawPrice > 0 ? formatPrice(rawPrice, grandTotalCurrency.label, locale.id) : undefined
    }

    const storeCreditPayment = getStoreCredit(state)
    const total = priceFromPriceObj(subtractPriceObjs(grandTotal, storeCreditPayment))
    const shippingCharge = format(totalShippingCharge)
    const savingsTotal = format(x_totalSavings)
    const salesTax = format(totalSalesTax)
    const orderTaxes = getOrderTaxes(totalTaxByTaxCategory, salesTax, locale.id, lineLevelTaxesEnabled)
    const rawOrderTaxes = getOrderTaxes(totalTaxByTaxCategory, salesTax, locale.id, lineLevelTaxesEnabled, true)

    //Analytics fields
    const priceWithPromotion = priceFromPriceObj(x_totalProductPriceWithPromotion)
    const rawPriceWithTax = addPriceObjs(x_totalProductPriceWithPromotion, totalSalesTax)
    const priceWithTax = format(rawPriceWithTax) || 0
    const priceWithShippingTax = priceFromPriceObj(grandTotal)

    let itemTotal
    let rawItemTotal
    let totalTaxesDuties
    let displaySalesTax

    if (lineLevelTaxesEnabled && (locale?.country !== 'US' || !showUsTaxUi)) {
        itemTotal = format(x_sumLineTotal)
        rawItemTotal = priceFromPriceObj(x_sumLineTotal)
    } else {
        // TON & US
        itemTotal = format(x_totalProductPriceWithPromotion)
        rawItemTotal = priceFromPriceObj(x_totalProductPriceWithPromotion)
        totalTaxesDuties = format(x_totalDDP)
        displaySalesTax = salesTax
    }

    let shippingPrice
    const shippingTaxObject =
        priceFromPriceObj(totalShippingTax) > 0
            ? totalShippingTax
            : totalTaxByTaxCategory && totalTaxByTaxCategory.find(({ taxCategoryCode }) => taxCategoryCode === 'SHIPPING_VAT')?.value

    const totalShippingPrice = addPriceObjs(totalShippingCharge, shippingTaxObject)

    if (x_adjustedTotalShippingChargeIncTax) {
        shippingPrice = getShippingPrice(totalShippingPrice, x_adjustedTotalShippingChargeIncTax, totalShippingChargeCurrency, messages, locale)
    } else {
        const totalShippingWithAdjustmentPrice = addPriceObjs(x_totalShippingChargeWithAdjustment, shippingTaxObject)

        shippingPrice = getShippingPrice(totalShippingPrice, totalShippingWithAdjustmentPrice, totalShippingChargeCurrency, messages, locale)
    }

    const codCharge = cart?.adjustment
        ?.filter((adjustment) => adjustment.displayLevel === 'Order' && adjustment.code === 'COD_CHARGE')
        ?.find((adjustment) => adjustment.amount)?.amount
    const codAmount = codCharge && priceFromPriceObj(codCharge)
    const codChargeAmount = codAmount && codAmount > 0 && formatPrice(codAmount, grandTotalCurrency.label, locale.id)

    const totalProductPriceValue = priceFromPriceObj(totalProductPrice) || 0
    const totalAdjustmentValue = priceFromPriceObj(totalAdjustment) || 0
    const itemSubtotalExTaxDuty = totalProductPriceValue + totalAdjustmentValue
    const [shippingOptions] = getShippingOptions(state, props)
    const selectedShippingOption = shippingOptions?.find(({ selected }) => selected) || null
    const { promotions, rewards } = getPromotionsAndRewards(promotionCode, adjustment, messages, locale.id)

    return {
        currency: grandTotalCurrency.label,
        ...shippingPrice,
        itemTotal,
        rawItemTotal,
        promotions,
        rewards,
        tax: displaySalesTax,
        rawTax: priceFromPriceObj(totalSalesTax),
        duty: totalTaxesDuties,
        taxDutyInfo: taxDutyInfo(),
        total: formatPrice(total, grandTotalCurrency.label, locale.id),
        rawTotal: total,
        grandTotal: formatPrice(priceFromPriceObj(grandTotal), grandTotalCurrency.label, locale.id),
        shippingCharge,
        storeCredit: storeCreditPayment && storeCreditPayment.formattedAmount,
        rawStoreCredit: storeCreditPayment && storeCreditPayment.amount / storeCreditPayment.divisor,
        savingsTotal,
        orderTaxes,
        rawOrderTaxes,
        priceWithPromotion,
        priceWithTax,
        rawPriceWithTax: rawPriceWithTax && rawPriceWithTax.amount / rawPriceWithTax.divisor,
        priceWithShippingTax,
        codChargeAmount,
        itemSubtotalExTaxDuty,
        selectedShippingOption,
    }
}

export function getOrderTaxes(taxes: TaxByTaxCategory[] | undefined, salesTax: string | undefined, locale: string, lineLevelTaxesEnabled: boolean, raw?: boolean) {
    if (typeof taxes === 'undefined' || taxes === null || salesTax || !lineLevelTaxesEnabled) {
        return {}
    }

    let output = {}
    taxes.forEach(({ taxCategoryCode, value, currency }) => {
        if (value?.amount > 0) {
            output[taxCategoryCode] = raw ? priceFromPriceObj(value) : formatPrice(priceFromPriceObj(value), currency.label, locale)
        }
    })
    return output
}

export default getTotals
